import React, { useState } from 'react';
import {
    Text, 
    Theme,
    Services
} from '@simplybenefits/sb-ui-library';
import * as dates from '@simplybenefits/sb-dates';
import BenefitTile from './BenefitTile.js';
import Grid from '@mui/material/Grid';
import BenefitActionModal from './BenefitActionModal.js';
import { useDispatch } from "react-redux";
import { updateBenefitStatus, updateBenefitPremium } from '../../../../actions/users.js';

const ACTION_MAP = {
    TERMINATE_BENEFIT: "TERMINATE",
    REINSTATE_BENEFIT: "REINSTATE",
    WAIVE_PREMIUM: "WAIVE_PREMIUM",
    REINSTATE_PREMIUM: "REINSTATE_PREMIUM"
};

/**
 * 
 * @param {*} action 
 * @returns 
 */
const getActionType = (action) => {
    if (action === 'TERMINATE_BENEFIT' || action === 'REINSTATE_BENEFIT') {
        return 'BENEFIT_TYPE_ACTION';
    }

    return "PREMIUM_TYPE_ACTION";
};

/**
 * Renders a responsive grid of benefit tiles for a user's coverage, along with
 * an action modal for terminating or reinstating individual benefits.
 * @param {object}  props.coverage - Map of benefit name keys to their coverage data.
 * @param {object}  props.user - User metadata, including `userID`.
 * @returns {React.ReactElement}
 */
const BenefitTilesHandler = ({
    coverage,
    user
}) => {
    const [ actionModalOpen, setActionModalOpen ] = useState(false);
    const [ effectiveDate, setEffectiveDate ] = useState(null);
    const [ effectiveDateError, setEffectiveDateError ] = useState('');
    const [ selectedBenefitName, setSelectedBenefitName ] = useState(null);
    const [ action, setAction ] = useState(null);
    const dispatch = useDispatch();

    /**
     * Opens the action modal for a specific benefit and action type.
     * @param {'TERMINATE_BENEFIT'|'REINSTATE_BENEFIT'} modalAction - The action to perform.
     * @param {string} benefitName - The coverage key of the benefit (e.g. `'life'`).
     */
    const openActionModal = (modalAction, benefitName) => {
        setActionModalOpen(true);
        setAction(modalAction);
        setSelectedBenefitName(benefitName);
    };

    /**
     * Closes the action modal and resets all related state (action, selected benefit, effective date).
     */
    const closeActionModal = () => {
        setActionModalOpen(false);
        setAction(null);
        setSelectedBenefitName(null);
        setEffectiveDate(null);
    };

    /**
     * Returns the relevant coverage data for a given benefit name.
     * Spending accounts expose a nested `meta` object; all other benefits return the top-level value.
     * @param {string} name - The benefit key from the coverage map.
     * @returns {object} The benefit's coverage data.
     */
    const returnBenefitData = (name) => name === 'spendingAccounts'
        ? coverage[name].meta 
        : coverage[name];

    /**
     * Handles confirmation in the action modal. Dispatches the toggle action for the selected
     * benefit using the chosen effective date, then reloads the page on success.
     * @returns {Promise<void>}
     */
    const onConfirm = async () => {
        const actionType = getActionType(action);
        const updateFn = actionType === "BENEFIT_TYPE_ACTION" 
            ? updateBenefitStatus
            : updateBenefitPremium;

        const success = await dispatch(updateFn(user.userID, selectedBenefitName, dates.getUTC(effectiveDate), ACTION_MAP[action])); 

        if (success) {
            closeActionModal();
        }
    };

    /**
     * Validates the effective date held in state via the shared input validation service.
     * Sets an error message in state if the date is invalid.
     * @returns {Promise<boolean>} `true` if the date is valid, `false` otherwise.
     */
    const validateEffectiveDate = async () => {
        const effectiveDateErrorVerbiage = await Services.validateInput('effectiveDate', effectiveDate);
        setEffectiveDateError(effectiveDateErrorVerbiage);
    
        if (effectiveDateErrorVerbiage) {
            return false;
        }
    
        return true;
    };

    const isCoveragePopulated = !Object.values(coverage).every((coverageCategory) => Object.keys(coverageCategory).length === 0);

    return (
        <>
            {
                isCoveragePopulated 
                    ? (
                        <Grid container spacing={3}>
                            {
                                Object.keys(coverage).map((name, index) => (
                                    <Grid item xs={12} sm={6} md={4} lg={4} xl={4} key={index}>
                                        <BenefitTile 
                                            benefitName={name} 
                                            benefitData={returnBenefitData(name)} 
                                            userMeta={user}
                                            openActionModal={openActionModal}
                                            closeActionModal={closeActionModal}
                                        />
                                    </Grid>
                                ))
                            }
                        </Grid>
                    )
                    : (
                        <Text medium color={Theme.colors.mainTextGrey}>
                            Coverage records have not been populated.
                        </Text>
                    )
            }

            { 
                selectedBenefitName && (
                    <BenefitActionModal
                        open={actionModalOpen}
                        onClose={closeActionModal}
                        onConfirm={onConfirm}
                        effectiveDate={effectiveDate}
                        setEffectiveDate={setEffectiveDate}
                        effectiveDateError={effectiveDateError}
                        validateEffectiveDate={validateEffectiveDate}
                        action={action}
                        selectedBenefitName={selectedBenefitName}
                        user={user}
                    />
                )
            }
        </>
    );
};

export default BenefitTilesHandler;